import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import WeatherDayModal from "./WeatherDayModal";
import "../styles/components/TripWeatherCard.css";
import API_BASE, { withApiCredentials } from "../api";

function withTripApiCredentials(options = {}) {
  const token =
    typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
  if (!token) {
    return withApiCredentials(options);
  }

  const headers = new Headers(options.headers || {});
  headers.set("Authorization", `Bearer ${token}`);
  return withApiCredentials({
    ...options,
    headers,
  });
}

// --- ROBUST HELPER: Handles both String Names AND Numeric Codes ---
const getWeatherIcon = (day) => {
  // 1. Try using the string name (New Backend)
  const iconName = day.icon;
  const map = {
    "clear": "\u2600\uFE0F",
    "partly-cloudy": "\u26C5",
    "cloudy": "\u2601\uFE0F",
    "rain": "\uD83C\uDF27\uFE0F",
    "showers": "\uD83C\uDF26\uFE0F",
    "storm": "\u26C8\uFE0F",
    "snow": "\u2744\uFE0F",
    "fog": "\uD83C\uDF2B\uFE0F",
    "drizzle": "\uD83D\uDCA7"
  };
  if (map[iconName]) return map[iconName];

  // 2. Fallback: Map Numeric Codes (Old Backend / Cache)
  const code = day.weatherCode;
  if (code === 0) return "\u2600\uFE0F";
  if (code > 0 && code < 40) return "\u26C5"; // 1, 2, 3
  if (code >= 40 && code < 50) return "\uD83C\uDF2B\uFE0F"; // 45, 48
  if (code >= 50 && code < 60) return "\uD83D\uDCA7"; // 51-57
  if (code >= 60 && code < 70) return "\uD83C\uDF27\uFE0F"; // 61-67
  if (code >= 70 && code < 80) return "\u2744\uFE0F"; // 71-77
  if (code >= 80 && code < 85) return "\uD83C\uDF26\uFE0F"; // 80-82
  if (code >= 95) return "\u26C8\uFE0F";             // 95-99

  return "?";
};

const getMoonIcon = (phaseName) => {
  if (!phaseName) return "";
  const p = phaseName.toLowerCase();
  if (p.includes("new")) return "\uD83C\uDF11";
  if (p.includes("crescent")) return "\uD83C\uDF19";
  if (p.includes("quarter")) return "\uD83C\uDF13";
  if (p.includes("gibbous")) return "\uD83C\uDF14";
  if (p.includes("full")) return "\uD83C\uDF15";
  return "\uD83C\uDF11";
};

export default function TripWeatherCard({
  conditions,
  tripId,
  userId,
  onRefresh,
  allowRefresh = true,
  isPastTripView = false,
  selectedDay: controlledSelectedDay = null,
  onSelectedDayChange = null,
}) {
  const { t } = useTranslation();
  const [selectedDayState, setSelectedDayState] = useState(null);
  const [loading, setLoading] = useState(false);
  const selectedDay = controlledSelectedDay ?? selectedDayState;
  const setSelectedDay = onSelectedDayChange || setSelectedDayState;

  // --- REFRESH HANDLER ---
  const handleRefresh = async () => {
    if (!tripId) return;
    setLoading(true);
    try {
        console.log("🔄 Force refreshing weather for trip:", tripId);
        
        const res = await fetch(
          `${API_BASE}/trips/${tripId}/conditions?userId=${encodeURIComponent(userId)}&force_refresh=true`,
          withTripApiCredentials()
        );
        
        if (res.ok) {
            const data = await res.json();
            if (onRefresh) onRefresh(data); 
        } else {
            console.error("Refresh failed with status:", res.status);
        }
    } catch (err) {
        console.error("Refresh request failed", err);
    } finally {
        setLoading(false);
    }
  };

  if (!conditions) return null;

  // Check mode safely
  const isForecast = conditions.mode === "forecast";
  const isForecastTemporarilyUnavailable =
    conditions.status === "rate_limited" ||
    conditions.status === "temporarily_unavailable";
  const isRefreshCoolingDown = conditions.status === "rate_limited";
  const isFallbackWeather =
    conditions.fallbackType === "historical_average" ||
    conditions.forecastSource === "historical_average";
  const weatherUnavailableMessage =
    conditions.message ||
    t("weather_forecast_temporarily_unavailable", {
      defaultValue: "Forecast temporarily unavailable. Please try again later.",
    });
  const fallbackLabel =
    conditions.fallbackLabel ||
    t("weather_estimated_seasonal", {
      defaultValue: "Estimated seasonal weather",
    });
  const cooldownLabel = t("weather_refresh_try_later", {
    defaultValue: "Try again later",
  });
  const showRefresh = allowRefresh && tripId;
  
  let title = t("weather_seasonal_average");
  if (isFallbackWeather) {
     title = fallbackLabel;
  } else if (isForecast) {
     title = t("weather_forecast_14_day");
  } else if (isPastTripView) {
     title = t("weather_historical_conditions", {
       defaultValue: "Historical Conditions"
     });
  }

  return (
    <>
      <div className="trip-weather-card">
        <div className="weather-card-header">
          <div className="weather-title-group">
            <h4 className="weather-title">
                {isForecast ? "\uD83D\uDCC5" : "\uD83D\uDCCA"} {title}
            </h4>
            {isFallbackWeather ? (
              <span className="weather-live-badge weather-estimate-badge">
                {t("weather_estimate_badge", { defaultValue: "ESTIMATE" })}
              </span>
            ) : (
              isForecast && (
                <span className="weather-live-badge">
                  {t("weather_live_badge", { defaultValue: "LIVE" })}
                </span>
              )
            )}
          </div>

          {/* REFRESH BUTTON */}
          {showRefresh && (
            <button 
                className={`weather-refresh-btn ${loading ? "spinning" : ""}`} 
                onClick={handleRefresh}
                disabled={loading || isRefreshCoolingDown}
                title={
                  isRefreshCoolingDown
                    ? cooldownLabel
                    : t("actions.refresh_forecast")
                }
            >
                {loading ? "\u23F3" : isRefreshCoolingDown ? "\u23F1" : "\uD83D\uDD04"}
            </button>
          )}
        </div>

        {isForecastTemporarilyUnavailable && (
          <p className="weather-status-note">
            {weatherUnavailableMessage}
            {isRefreshCoolingDown ? ` ${cooldownLabel}.` : ""}
          </p>
        )}

        {conditions.days && conditions.days.length > 0 ? (
          <div className="weather-scroll-container">
            {conditions.days.map((day, i) => {
              const d = new Date(day.date);
              const dayName = d.toLocaleDateString(undefined, { weekday: 'short' });
              const dateStr = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

              return (
                <div 
                  key={i} 
                  className="weather-day-column clickable" 
                  onClick={() => setSelectedDay(day)}
                >
                  <div className="day-header">
                    <span className="day-name">{dayName}</span>
                    <span className="day-date">{dateStr}</span>
                  </div>
                  
                  {/* UPDATED ICON CALL */}
                  <div
                    className="weather-icon-large"
                    title={
                      day.description ||
                      t("weather_info_title", { defaultValue: "Weather info" })
                    }
                  >
                    {getWeatherIcon(day)}
                  </div>
                  
                  <div className="temp-stack">
                    <span className="temp-high">{Math.round(day.tempMax)}{"\u00B0"}</span>
                    <span className="temp-low">{Math.round(day.tempMin)}{"\u00B0"}</span>
                  </div>

                  <div className="wind-stack">
                    <span className="wind-speed">{"\uD83D\uDCA8"} {Math.round(day.windSpeed)}</span>
                    <span className="wind-dir">
                      {day.windDir ||
                        t("weather_wind_variable_short", { defaultValue: "Var" })}
                    </span>
                  </div>

                  {/* Moon Check: Only show if backend sends it */}
                  {day.moon && (
                    <div className="moon-footer">
                      {getMoonIcon(day.moon.phase)}
                    </div>
                  )}
                  
                  {!day.isForecast && (
                     <span className="weather-avg-label">
                       {isPastTripView
                         ? t("weather_actual_short", { defaultValue: "Actual" })
                         : t("weather_average_short", { defaultValue: "Avg" })}
                     </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="weather-empty-state">
             <p>
               {isForecastTemporarilyUnavailable
                 ? weatherUnavailableMessage
                 : t("weather_no_data")}
             </p>
             {showRefresh && (
                <button
                  onClick={handleRefresh}
                  className="weather-refresh-btn-large"
                  disabled={loading || isRefreshCoolingDown}
                >
                    {isRefreshCoolingDown
                      ? cooldownLabel
                      : `\uD83D\uDD04 ${t("actions.refresh_forecast")}`}
                </button>
             )}
          </div>
        )}
      </div>

      {selectedDay && (
        <WeatherDayModal 
          day={selectedDay} 
          onClose={() => setSelectedDay(null)} 
          isPastTripView={isPastTripView}
          timezone={conditions.timezone}
        />
      )}
    </>
  );
}
